import React from 'react';
import { Flex, Typography } from 'antd';
import { Doughnut } from 'react-chartjs-2';
import { EyeInvisibleOutlined } from '@ant-design/icons';

import { CrawlerType, crawlerTypeLabel } from '~features/crawler-filter';
import { ColorsList, percentageFormatter } from '../Chart';
import { LegendLabelSquare } from '~components/Charts';
import TOOLTIPS from './tooltips';
import { type ChartItemsVisibilityProps } from '~library/chartjs';
import { getPercentage } from '~library/numbers';
import { useIsPrinting } from '~library/print';

export const SIDE_CHART_ID = 'crawler-type-side-chart';

type CrawlerTypeSideChartProps = {
  totalRequestPerType: [CrawlerType, number][];
} & ChartItemsVisibilityProps;

function CrawlerTypeSideChart({ totalRequestPerType, itemsVisibility, onItemClicked }: CrawlerTypeSideChartProps) {
  const { isPrinting } = useIsPrinting();
  const totalVisibleRequests = React.useMemo(() => {
    return totalRequestPerType.reduce((acc, [_, value], i) => {
      if (itemsVisibility[i]) {
        acc += value;
      }
      return acc;
    }, 0);
  }, [totalRequestPerType, itemsVisibility]);

  const crawlerTypesPercentagesData = React.useMemo(() => {
    const data = totalRequestPerType.reduce<{
      labels: string[];
      datasets: {
        data: number[];
        backgroundColor: string[];
      }[];
    }>(
      (acc, [name, value], i) => {
        acc.labels.push(crawlerTypeLabel(name));
        acc.datasets[0].data.push(getPercentage(value, totalVisibleRequests));
        acc.datasets[0].backgroundColor.push(ColorsList[i]);
        return acc;
      },
      {
        labels: [],
        datasets: [
          {
            data: [],
            backgroundColor: [],
          },
        ],
      }
    );
    return data;
  }, [totalRequestPerType, totalVisibleRequests]);

  return (
    <Flex justify="space-between" vertical style={{ height: '100%' }}>
      <div>
        {totalRequestPerType.map(([type], i) => (
          <Flex justify="space-between" key={type}>
            <LegendLabelSquare
              small
              color={ColorsList[i]}
              label={crawlerTypeLabel(type)}
              tooltipContent={TOOLTIPS[type]}
              crossedOut={!itemsVisibility[i]}
              onClick={() => onItemClicked(i)}
            />
            {itemsVisibility[i] ? (
              <Typography.Text>{percentageFormatter(crawlerTypesPercentagesData.datasets[0].data[i])}</Typography.Text>
            ) : (
              <EyeInvisibleOutlined />
            )}
          </Flex>
        ))}
      </div>

      <div style={{ height: 156, minWidth: 156 }}>
        <Doughnut
          id={SIDE_CHART_ID}
          data={crawlerTypesPercentagesData}
          options={{
            animation: isPrinting ? false : undefined,
            plugins: {
              tooltip: {
                callbacks: {
                  label: (context) => percentageFormatter(context.parsed),
                },
              },
            },
          }}
        />
      </div>
    </Flex>
  );
}

export default CrawlerTypeSideChart;
